import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import Header from '../Header';
import { apiService } from '../../services/api';
import type { ArDisease, ArIndication, ArWorkbookParsed } from '../../types';
import { buildTracker, TrackerRow } from './arHelpers';
import OverviewTab from './OverviewTab';
import LandscapeTab from './LandscapeTab';
import TimelineTab from './TimelineTab';
import StudySitesTab from './StudySitesTab';
import ByTargetView from './ByTargetView';
import './radar.css';

export type ArTabKey = 'overview' | 'landscape' | 'timeline' | 'sites';
const TABS: { key: ArTabKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'landscape', label: 'Landscape' },
  { key: 'timeline', label: 'Timeline' },
  { key: 'sites', label: 'Study sites' },
];

export interface ArSharedProps {
  disease: ArDisease | null;
  workbook: ArWorkbookParsed | null;
  ind: ArIndication | null;
  tracker: TrackerRow[] | null;
  trackerError: boolean;
}

const AssetRadarPage: React.FC = () => {
  const { tab: tabParam } = useParams<{ tab?: string }>();
  const tab: ArTabKey = (TABS.some((t) => t.key === tabParam) ? tabParam : 'overview') as ArTabKey;
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [diseases, setDiseases] = useState<ArDisease[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [isSuperuser, setIsSuperuser] = useState(false);

  const [workbook, setWorkbook] = useState<ArWorkbookParsed | null>(null);
  const [wbLoading, setWbLoading] = useState(false);
  const [wbError, setWbError] = useState<string | null>(null);

  const [tracker, setTracker] = useState<TrackerRow[] | null>(null);
  const [trackerError, setTrackerError] = useState(false);

  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);

  const diseaseKey = params.get('disease') || '';
  const settingKey = params.get('setting') || '';
  const mode: 'indication' | 'target' = params.get('mode') === 'target' ? 'target' : 'indication';
  const setMode = (m: 'indication' | 'target') => {
    const p = new URLSearchParams(params);
    if (m === 'target') p.set('mode', 'target'); else p.delete('mode');
    setParams(p);
  };

  // Load disease list + current user (for the superuser-only Sync button) once.
  useEffect(() => {
    apiService.arGetDiseases().then((list) => {
      setDiseases(list);
      if (!diseaseKey && list.length) {
        const sorted = [...list].filter((d) => d.versions.length);
        if (sorted.length) {
          const p = new URLSearchParams(params);
          p.set('disease', sorted[0].key);
          setParams(p, { replace: true });
        }
      }
    }).catch((e) => setListError(e?.response?.data?.error || e?.message || 'Could not load Asset Radar diseases'));
    apiService.getCurrentUser().then((u) => setIsSuperuser(Boolean(u?.is_superuser))).catch(() => {});
    apiService.arGetTracker().then((rows) => setTracker(buildTracker(rows))).catch(() => setTrackerError(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const disease = useMemo(() => (diseases || []).find((d) => d.key === diseaseKey) || null, [diseases, diseaseKey]);
  const fileId = disease?.versions?.[0]?.file_id || null;
  const file = disease?.versions?.[0] || null;

  useEffect(() => {
    if (!fileId) { setWorkbook(null); return; }
    let cancelled = false;
    // Clear the old workbook immediately so the loading spinner below (keyed on `!workbook`) actually
    // shows instead of leaving the previous disease's tab content on screen -- matching the same fix in
    // TimelineTab's indication-switch effect.
    setWorkbook(null);
    setWbLoading(true); setWbError(null);
    apiService.arGetWorkbook(fileId).then((wb) => {
      if (cancelled) return;
      setWorkbook(wb);
      const inds = wb.inds || [];
      if (inds.length && !inds.some((i) => i.key === settingKey)) {
        const p = new URLSearchParams(params);
        p.set('setting', inds[0].key);
        setParams(p, { replace: true });
      }
    }).catch((e) => { if (!cancelled) setWbError(e?.response?.data?.error || e?.message || 'Could not load this workbook'); })
      .finally(() => { if (!cancelled) setWbLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId]);

  const ind = useMemo(() => {
    if (!workbook || !workbook.inds?.length) return null;
    return workbook.inds.find((i) => i.key === settingKey) || workbook.inds[0];
  }, [workbook, settingKey]);

  const selectDisease = (key: string) => {
    const p = new URLSearchParams(params);
    p.set('disease', key);
    p.delete('setting');
    setParams(p);
  };
  const selectSetting = (key: string) => {
    const p = new URLSearchParams(params);
    p.set('setting', key);
    setParams(p);
  };

  // By-target asset card footer chips jump back to the normal per-disease Landscape view, at the exact
  // disease/setting/asset the chip named -- mirrors OverviewTab's own onJump pattern below, just also
  // crossing disease (by-target has no single-disease scope to begin with).
  const jumpToIndication = (opts: { disease: string; setting: string; asset: string }) => {
    const p = new URLSearchParams(params);
    p.delete('mode');
    p.set('disease', opts.disease);
    p.set('setting', opts.setting);
    p.set('asset', opts.asset);
    navigate(`/asset-radar/landscape?${p.toString()}`);
  };

  const runSync = async (force: boolean) => {
    setSyncing(true); setSyncMsg('Sync started…');
    try {
      await apiService.arStartSync(force);
      const poll = async () => {
        try {
          const run = await apiService.arGetLatestSync();
          if (!run) { setSyncing(false); return; }
          if (run.status === 'running' || run.status === 'queued') {
            setSyncMsg('Sync running…');
            pollRef.current = window.setTimeout(poll, 4000);
          } else {
            setSyncing(false);
            setSyncMsg(run.status === 'failed' ? 'Sync failed — see server logs.' : 'Sync complete.');
            apiService.arGetDiseases().then(setDiseases).catch(() => {});
            if (run.status !== 'failed') {
              // The workbook/timeline/trial-detail caches (services/api.ts) would otherwise keep
              // showing pre-sync data for the rest of this session -- drop them and refetch what's
              // currently on screen so a sync's results actually show up without a page reload.
              apiService.arClearRadarCache();
              if (fileId) apiService.arGetWorkbook(fileId).then(setWorkbook).catch(() => {});
            }
          }
        } catch { setSyncing(false); setSyncMsg('Could not check sync status.'); }
      };
      poll();
    } catch (e: any) {
      setSyncing(false);
      setSyncMsg(e?.response?.data?.error || 'Could not start sync.');
    }
  };
  useEffect(() => () => { if (pollRef.current) window.clearTimeout(pollRef.current); }, []);

  const keys = useMemo(() => (diseases || []).filter((d) => d.versions.length).sort((a, b) => a.label.localeCompare(b.label)), [diseases]);

  const sharedProps: ArSharedProps = { disease, workbook, ind, tracker, trackerError };

  // Most recent data cutoff across the workbook's settings (falls back to the version's CCOD string).
  let cutoffText = '';
  if (workbook) {
    for (const i of workbook.inds) {
      const t = i.land?.cutoff;
      if (t && (!cutoffText || t > cutoffText)) cutoffText = t;
    }
  }
  if (!cutoffText && file?.version && /CCOD\s+(.+)$/i.test(file.version)) {
    cutoffText = (file.version.match(/CCOD\s+(.+)$/i) as RegExpMatchArray)[1];
  }
  const isDraft = file?.status === 'draft';

  const modeToggle = (
    <div className="modebar">
      <div className="vtabs" role="tablist" aria-label="Browse by">
        <button type="button" className="vp" role="tab" aria-selected={mode === 'indication'} onClick={() => setMode('indication')}>By indication</button>
        <button type="button" className="vp" role="tab" aria-selected={mode === 'target'} onClick={() => setMode('target')}>By target</button>
      </div>
    </div>
  );

  if (mode === 'target') {
    return (
      <div className="asset-radar" data-superuser={isSuperuser ? '1' : '0'}>
        <Header />
        <div className="page">
          {modeToggle}
          <ByTargetView diseases={keys} tracker={tracker} onJumpToIndication={jumpToIndication} />
        </div>
      </div>
    );
  }

  return (
    <div className="asset-radar" data-superuser={isSuperuser ? '1' : '0'}>
      <Header />
      <div className="page">
        {modeToggle}
        <header id="topbar">
          <div className="eyebrow">Tracked indication</div>
          {keys.length > 0 ? (
            <h1 className="h1">
              <select className="title-sel" aria-label="Disease" value={diseaseKey} onChange={(e) => selectDisease(e.target.value)}>
                {keys.map((d) => (
                  <option key={d.key} value={d.key}>{d.label}</option>
                ))}
              </select>
            </h1>
          ) : (
            <h1 className="h1">{listError ? 'Could not load Asset Radar' : diseases ? 'No landscapes yet' : 'Loading database…'}</h1>
          )}
          <div className="pills">
            {workbook && workbook.inds?.length > 0 && (
              tab === 'overview' ? (
                <span className="pill" title="Overview combines every setting for this disease">All settings</span>
              ) : (
                <select className="pill" aria-label="Setting" value={ind?.key || ''} onChange={(e) => selectSetting(e.target.value)}>
                  {workbook.inds.map((i) => (
                    <option key={i.key} value={i.key}>{i.label}</option>
                  ))}
                </select>
              )
            )}
            {cutoffText && (
              <span className="cutoff" title={`Most recent data cutoff in this workbook${file?.title ? ` (${file.title})` : ''}`}>
                Data cutoff <b>{cutoffText}</b>{isDraft ? ' · draft' : ''}
              </span>
            )}
            {!cutoffText && isDraft && <span className="cutoff" title={file?.title || ''}>Draft</span>}
            {workbook?.synced_at && (
              <span className="meta">Synced {new Date(workbook.synced_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
            )}
            {isSuperuser && (
              <>
                <span className="spacer" />
                <button type="button" className="btn dark" disabled={syncing} onClick={() => runSync(false)}>
                  <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
                  {syncing ? 'Syncing…' : 'Sync now'}
                </button>
                {syncMsg && <span className="meta">{syncMsg}</span>}
              </>
            )}
          </div>
        </header>

        <section className="panel" aria-label="Landscape workspace">
          <div id="viewbar">
            <div className="vpills vbar">
              <div className="vtabs" role="tablist" aria-label="View">
                {TABS.map((t) => (
                  <Link
                    key={t.key}
                    to={`/asset-radar/${t.key}${params.toString() ? `?${params.toString()}` : ''}`}
                    className="vp"
                    role="tab"
                    aria-selected={tab === t.key}
                  >
                    {t.label}
                  </Link>
                ))}
              </div>
              <div id="phTools" className="phtools" />
              <div className="spacer" />
            </div>
          </div>

          <div className="content" aria-live="polite">
            {listError && !diseases?.length && (
              <div className="state"><h2>Couldn&rsquo;t load Asset Radar data</h2><p>{listError}</p></div>
            )}
            {diseases && !keys.length && !listError && (
              <div className="state">
                <h2>No landscapes yet</h2>
                <p>{isSuperuser
                  ? 'Nothing has been synced from Google Sheets yet. Use Sync now above to load the workbooks.'
                  : 'Nothing has been synced from Google Sheets yet. Ask an administrator to run a sync.'}</p>
              </div>
            )}
            {wbError && <div className="state"><h2>Workbook couldn&rsquo;t be loaded</h2><p>{wbError}</p></div>}
            {diseaseKey && (wbLoading || !workbook) && !wbError && (
              <div className="state"><div className="spin" /><p>Loading database…</p></div>
            )}
            {workbook && !ind && (
              <div className="state">
                <h2>No landscape tabs in this workbook</h2>
                <p>This workbook has no tabs named &ldquo;&hellip; Landscape&rdquo; or &ldquo;&hellip; Calendar&rdquo;. Pick another disease or version.</p>
              </div>
            )}

            {workbook && ind && (
              <>
                {tab === 'overview' && (
                  <OverviewTab
                    {...sharedProps}
                    onJump={(opts) => {
                      const p = new URLSearchParams(params);
                      if (opts.setting) p.set('setting', opts.setting); else p.delete('setting');
                      if (opts.q != null) p.set('q', opts.q); else p.delete('q');
                      if (opts.asset != null) p.set('asset', opts.asset); else p.delete('asset');
                      navigate(`/asset-radar/${opts.tab}?${p.toString()}`);
                    }}
                  />
                )}
                {tab === 'landscape' && <LandscapeTab {...sharedProps} />}
                {tab === 'timeline' && <TimelineTab {...sharedProps} />}
                {tab === 'sites' && <StudySitesTab {...sharedProps} />}
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AssetRadarPage;
